import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Camera, FileText, Wallet, FolderOpen, Inbox, Mail, ClipboardCopy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Input, Select, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { Stars } from "@/pages/Reviews";
import { REPLY_TEMPLATES } from "@/lib/replyTemplates";
import { INTEREST_OPTIONS } from "@/lib/planner";

const DEMO_NOTE = "DEMO";

const REC_CATEGORIES = ["beach", "dining", "activity", "attraction", "spa", "nightlife", "shopping"];
const REC_PARISHES = ["St. George's", "Hamilton Parish", "Smith's", "Hamilton", "Pembroke", "Devonshire", "Paget", "Warwick", "Southampton", "Sandys"];
const REC_AUDIENCES = ["general", "couples", "kids", "luxury", "cruise"];
const REC_AGES = ["toddler", "kid", "teen", "adult"];
const EMPTY_REC = { name: "", category: "activity", parish: "Hamilton", description: "", website: "", booking_url: "", price_level: 2, duration_hours: 2, audiences: ["general"], interests: [], ages: ["kid", "teen", "adult"], rainy_day: false, beach_day: false, lat: "", lng: "", active: true };

const LEAD_STATUS_STYLES = {
  new: "bg-bermuda/20 text-bermuda-dark",
  in_progress: "bg-ocean-light text-ocean",
  won: "bg-sage/20 text-sage",
  closed: "bg-navy/10 text-navy/50",
};

const Stat = ({ label, value, sub }) => (
  <div className="border border-navy/10 bg-white p-6" data-testid={`owner-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy/45">{label}</p>
    <p className="mt-2 font-serif text-3xl text-navy">{value}</p>
    {sub && <p className="mt-1 text-xs text-navy/50">{sub}</p>}
  </div>
);

const Panel = ({ title, badge, children, testid }) => (
  <div className="border border-navy/10 bg-white" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-navy/10 px-6 py-4">
      <h3 className="font-serif text-lg text-navy">{title}</h3>
      {badge && <span className="bg-sand px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-navy/60">{badge}</span>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const LeadRow = ({ l, ctx }) => (
  <li className="py-4" data-testid={`lead-item-${l.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm font-medium capitalize text-navy">{l.type.replace(/_/g, " ")}</p>
      <div className="flex flex-wrap items-center gap-3">
        {l.type === "ebook_order" && (
          <span
            data-testid={`lead-payment-badge-${l.id}`}
            className={`px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${l.payment_status === "confirmed" ? "bg-sage/20 text-sage" : "bg-bermuda/20 text-bermuda-dark"}`}
          >
            {l.payment_status === "confirmed" ? "Paid" : "Awaiting Payment"}
          </span>
        )}
        <Select
          data-testid={`lead-status-${l.id}`}
          value={l.status || "new"}
          onChange={(e) => ctx.setLeadStatus(l.id, e.target.value)}
          className={`!w-auto !border-0 !px-2.5 !py-1.5 !text-[9px] font-bold uppercase tracking-wider ${LEAD_STATUS_STYLES[l.status || "new"]}`}
        >
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="won">Won</option>
          <option value="closed">Closed</option>
        </Select>
        {l.replies?.length > 0 && (
          <span className="bg-ocean-light px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-ocean" data-testid={`lead-replied-badge-${l.id}`}>
            Replied ×{l.replies.length}
          </span>
        )}
        <p className="text-xs text-navy/45">{l.created_at.slice(0, 10)}</p>
      </div>
    </div>
    <p className="mt-1.5 text-xs leading-relaxed text-navy/60">
      {Object.entries(l.data).slice(0, 5).map(([k, v]) => `${k.replace(/_/g, " ")}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
    </p>
    {l.replies?.length > 0 && (
      <div className="mt-2.5 space-y-1.5 border-l-2 border-ocean/30 pl-3">
        {l.replies.map((r) => (
          <p key={r.id} className="text-[11px] leading-relaxed text-navy/50" data-testid={`lead-reply-item-${r.id}`}>
            <span className="font-medium text-navy/65">You replied {r.created_at.slice(0, 10)}:</span> {r.message}
          </p>
        ))}
      </div>
    )}
    {l.type === "ebook_order" && l.payment_status !== "confirmed" && (
      <>
        <button
          onClick={() => ctx.confirmPayment(l.id)}
          disabled={ctx.confirming[l.id]}
          data-testid={`lead-confirm-payment-${l.id}`}
          className="mt-2.5 mr-2 bg-sage/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-sage disabled:opacity-60"
        >
          {ctx.confirming[l.id] ? "Confirming…" : "Confirm Payment & Send Guide"}
        </button>
        <button
          onClick={() => ctx.sendReminder(l.id)}
          disabled={ctx.reminding[l.id]}
          data-testid={`lead-remind-payment-${l.id}`}
          className="mt-2.5 mr-2 border border-bermuda-dark/40 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-bermuda-dark transition-colors hover:bg-bermuda-dark hover:text-white disabled:opacity-60"
        >
          {ctx.reminding[l.id] ? "Sending…" : "Send Payment Reminder"}
        </button>
        {l.payment_reminders?.length > 0 && (
          <span className="text-[10px] text-navy/45" data-testid={`lead-reminded-badge-${l.id}`}>Reminded ×{l.payment_reminders.length}</span>
        )}
      </>
    )}
    {l.data?.email && (
      ctx.replyFor === l.id ? (
        <div className="mt-3" data-testid={`lead-reply-box-${l.id}`}>
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`reply-templates-${l.id}`}>
            {REPLY_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`reply-template-${t.id}`}
                onClick={() => ctx.setReplyText(t.body.replaceAll("{name}", l.data.first_name || (l.data.name || "there").split(" ")[0] || "there"))}
                className="border border-navy/15 bg-sand-light px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-navy/60 transition-colors hover:border-bermuda-dark hover:text-navy"
              >
                {t.label}
              </button>
            ))}
          </div>
          <Textarea
            rows={3}
            data-testid={`lead-reply-text-${l.id}`}
            value={ctx.replyText}
            onChange={(e) => ctx.setReplyText(e.target.value)}
            placeholder={`Write your reply to ${l.data.email}…`}
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => ctx.sendReply(l.id)}
              disabled={ctx.replySending || !ctx.replyText.trim()}
              data-testid={`lead-reply-send-${l.id}`}
              className="bg-navy px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-60"
            >
              {ctx.replySending ? "Sending…" : "Send Reply"}
            </button>
            <button
              onClick={() => { ctx.setReplyFor(null); ctx.setReplyText(""); }}
              data-testid={`lead-reply-cancel-${l.id}`}
              className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/50 transition-colors hover:text-navy"
            >
              Cancel
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-navy/40">Sends an email from The Bermuda Co-Host — their reply comes back to your inbox.</p>
        </div>
      ) : (
        <button
          onClick={() => { ctx.setReplyFor(l.id); ctx.setReplyText(""); }}
          data-testid={`lead-reply-btn-${l.id}`}
          className="mt-2.5 border border-navy/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory"
        >
          Reply
        </button>
      )
    )}
  </li>
);

const ComingSoon = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center py-8 text-center">
    <Icon size={22} strokeWidth={1.2} className="text-navy/30" />
    <p className="mt-3 text-sm font-medium text-navy/60">{label}</p>
    <p className="mt-1 max-w-[220px] text-xs leading-relaxed text-navy/40">
      This panel connects to your live management systems — coming soon.
    </p>
  </div>
);

function MaintenanceForm({ onDone }) {
  const [form, setForm] = useState({ property_name: "", category: "", urgency: "", description: "", dispatch_permission: false, spend_limit: "" });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return setPhoto(null);
    if (file.size > 2_500_000) return toast.error("Photo must be under 2.5MB");
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/owner/maintenance-requests", { ...form, photo });
      toast.success("Maintenance request submitted.");
      setForm({ property_name: "", category: "", urgency: "", description: "", dispatch_permission: false, spend_limit: "" });
      setPhoto(null);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="maintenance-request-form">
      <Field label="Property" required><Input data-testid="mr-property" required value={form.property_name} onChange={set("property_name")} placeholder="e.g. Coral House, Warwick" /></Field>
      <Field label="Issue Category" required>
        <Select data-testid="mr-category" required value={form.category} onChange={set("category")}>
          <option value="">Select…</option>
          {["Plumbing", "Electrical", "Air Conditioning", "Landscaping", "Appliance", "Internet / Wi-Fi", "Pest Control", "General Repair", "Other"].map((c) => <option key={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label="Urgency" required>
        <Select data-testid="mr-urgency" required value={form.urgency} onChange={set("urgency")}>
          <option value="">Select…</option><option>Emergency</option><option>Urgent — this week</option><option>Routine</option>
        </Select>
      </Field>
      <Field label="Spending Authorization Limit"><Input data-testid="mr-spend-limit" value={form.spend_limit} onChange={set("spend_limit")} placeholder="e.g. Up to $250 without further approval" /></Field>
      <div className="sm:col-span-2"><Field label="Description" required><Textarea data-testid="mr-description" required value={form.description} onChange={set("description")} /></Field></div>
      <div className="sm:col-span-2">
        <Field label="Photo (optional, max 2.5MB)"><Input data-testid="mr-photo" type="file" accept="image/*" onChange={onFile} className="w-full border border-navy/15 bg-white px-4 py-3 text-sm text-navy file:mr-4 file:border-0 file:bg-sand-light file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-wider file:text-navy" /></Field>
      </div>
      <label className="flex items-start gap-3 text-sm text-navy/75 sm:col-span-2">
        <input type="checkbox" data-testid="mr-dispatch-permission" checked={form.dispatch_permission} onChange={set("dispatch_permission")} className="mt-1 accent-[#0F1C2E]" />
        I authorize The Bermuda Co-Host to dispatch an approved contractor within my spending limit.
      </label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} data-testid="maintenance-request-submit" className="bg-navy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-60">
          {loading ? "Submitting…" : "Submit Maintenance Request"}
        </button>
      </div>
    </form>
  );
}

function CleaningForm({ onDone }) {
  const [form, setForm] = useState({ property_name: "", service_type: "", preferred_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/owner/cleaning-requests", form);
      toast.success("Cleaning request submitted.");
      setForm({ property_name: "", service_type: "", preferred_date: "", notes: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="cleaning-request-form">
      <Field label="Property" required><Input data-testid="cr-property" required value={form.property_name} onChange={set("property_name")} placeholder="e.g. Coral House, Warwick" /></Field>
      <Field label="Service Type" required>
        <Select data-testid="cr-service-type" required value={form.service_type} onChange={set("service_type")}>
          <option value="">Select…</option>
          {["Standard turnover", "Deep cleaning", "Owner arrival cleaning", "Post-maintenance cleaning", "Emergency turnover"].map((c) => <option key={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label="Preferred Date" required><Input data-testid="cr-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} /></Field>
      <Field label="Notes"><Input data-testid="cr-notes" value={form.notes} onChange={set("notes")} placeholder="Access notes, priorities…" /></Field>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} data-testid="cleaning-request-submit" className="bg-navy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-60">
          {loading ? "Submitting…" : "Request Cleaning"}
        </button>
      </div>
    </form>
  );
}

export default function OwnerDashboard() {
  usePageMeta("Owner Portal");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [inviting, setInviting] = useState({});
  const [manualInvite, setManualInvite] = useState({ name: "", email: "" });
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [confirming, setConfirming] = useState({});
  const [reminding, setReminding] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [digestSending, setDigestSending] = useState(false);
  const [recsList, setRecsList] = useState([]);
  const [recForm, setRecForm] = useState(null);
  const [recSaving, setRecSaving] = useState(false);
  const [tab, setTab] = useState("overview");
  const [tripStats, setTripStats] = useState(null);

  const saveRec = async (e) => {
    e.preventDefault();
    setRecSaving(true);
    try {
      const payload = {
        ...recForm,
        price_level: Number(recForm.price_level),
        duration_hours: Number(recForm.duration_hours),
        lat: recForm.lat === "" || recForm.lat == null ? null : Number(recForm.lat),
        lng: recForm.lng === "" || recForm.lng == null ? null : Number(recForm.lng),
      };
      if (recForm.id) await api.patch(`/owner/recommendations/${recForm.id}`, payload);
      else await api.post("/owner/recommendations", payload);
      toast.success(recForm.id ? "Recommendation updated." : "Recommendation added.");
      setRecForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecSaving(false);
    }
  };

  const toggleRecActive = async (r) => {
    try {
      await api.patch(`/owner/recommendations/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRec = async (id) => {
    if (!window.confirm("Delete this recommendation permanently?")) return;
    try {
      await api.delete(`/owner/recommendations/${id}`);
      toast.success("Recommendation deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      await api.post("/owner/digest/send");
      toast.success("Digest sent — check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDigestSending(false);
    }
  };

  const sendReminder = async (leadId) => {
    setReminding((s) => ({ ...s, [leadId]: true }));
    try {
      await api.post(`/owner/leads/${leadId}/payment-reminder`);
      toast.success("Reminder sent — a gentle nudge is on its way.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReminding((s) => ({ ...s, [leadId]: false }));
    }
  };

  const confirmPayment = async (leadId) => {
    setConfirming((s) => ({ ...s, [leadId]: true }));
    try {
      await api.post(`/owner/leads/${leadId}/confirm-payment`);
      toast.success("Payment confirmed — the download link is on its way to their inbox.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming((s) => ({ ...s, [leadId]: false }));
    }
  };

  const setLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/owner/leads/${leadId}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success("Enquiry updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendReply = async (leadId) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      await api.post(`/owner/leads/${leadId}/reply`, { message: replyText });
      toast.success("Reply sent — it's on its way to their inbox.");
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReplySending(false);
    }
  };

  const sendInvite = async (email, name) => {
    setInviting((s) => ({ ...s, [email]: true }));
    try {
      await api.post("/owner/review-invites", { email, name });
      toast.success(`Invitation sent to ${email}`);
      setManualInvite({ name: "", email: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting((s) => ({ ...s, [email]: false }));
    }
  };

  const load = async () => {
    try {
      const res = await api.get("/owner/dashboard");
      setData(res.data);
      const leadsRes = await api.get("/leads");
      setLeads(leadsRes.data);
      const reviewsRes = await api.get("/owner/reviews");
      setReviews(reviewsRes.data);
      const contactsRes = await api.get("/owner/invite-contacts");
      setContacts(contactsRes.data);
      const recsRes = await api.get("/owner/recommendations");
      setRecsList(recsRes.data);
      const tripStatsRes = await api.get("/owner/trip-analytics");
      setTripStats(tripStatsRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (user === false) navigate("/owner/login", { replace: true });
    else if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || !data) {
    return <main className="flex min-h-screen items-center justify-center bg-ivory"><p className="text-sm text-navy/50">Loading your portal…</p></main>;
  }

  const { stats, property, upcoming_reservations, cleaning_schedule, inspection_reports, statements, inventory_alerts } = data;
  const TABS = [["overview", "Overview"], ["requests", "My Requests"], ["new-maintenance", "Report an Issue"], ["new-cleaning", "Request Cleaning"], ["reviews", "Reviews"], ["recs", "Recs Database"], ["analytics", "Trip Analytics"], ["orders", "Orders"], ["leads", "Enquiries"]];
  const serviceLeads = leads.filter((l) => l.type !== "ebook_order");
  const orderLeads = leads.filter((l) => l.type === "ebook_order");
  const pendingOrders = orderLeads.filter((l) => l.payment_status !== "confirmed");
  const paidOrders = orderLeads.filter((l) => l.payment_status === "confirmed");
  const ordersRevenue = paidOrders.reduce((sum, l) => sum + (parseInt(String(l.data?.price || "").replace(/[^0-9]/g, ""), 10) || 0), 0);
  const newLeadsCount = serviceLeads.filter((l) => (l.status || "new") === "new").length;
  const filteredServiceLeads = statusFilter === "all" ? serviceLeads : serviceLeads.filter((l) => (l.status || "new") === statusFilter);
  const rowCtx = { replyFor, setReplyFor, replyText, setReplyText, replySending, sendReply, setLeadStatus, confirming, confirmPayment, reminding, sendReminder };

  return (
    <main className="min-h-screen bg-sand-light pb-24 pt-32" data-testid="owner-dashboard">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda-dark">Owner Portal</p>
            <h1 className="mt-2 font-serif text-4xl font-light text-navy">Welcome back, {data.owner?.name || "Owner"}.</h1>
            <p className="mt-2 text-sm text-navy/55">{property.name} · {property.location} · <span className="text-sage">{property.status}</span></p>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="owner-logout-btn" className="flex items-center gap-2 border border-navy/20 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
            <LogOut size={13} /> Sign Out
          </button>
        </div>

        <div className="mt-6 flex items-center gap-3 border border-bermuda/40 bg-pink-soft/60 px-5 py-3" data-testid="demo-data-banner">
          <span className="bg-bermuda px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-navy">{DEMO_NOTE}</span>
          <p className="text-xs text-navy/70">Dashboard figures below are sample data showing the portal experience. Requests you submit here are real and reach our team.</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="owner-dashboard-tabs">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`owner-tab-${key}`}
              className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${tab === key ? "bg-navy text-ivory" : "border border-navy/15 bg-white text-navy/60 hover:text-navy"}`}
            >
              {label}
              {key === "leads" && newLeadsCount > 0 && (
                <span data-testid="leads-unread-badge" className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${tab === "leads" ? "bg-bermuda text-navy" : "bg-bermuda-dark text-white"}`}>
                  {newLeadsCount}
                </span>
              )}
              {key === "orders" && pendingOrders.length > 0 && (
                <span data-testid="orders-pending-badge" className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${tab === "orders" ? "bg-bermuda text-navy" : "bg-bermuda-dark text-white"}`}>
                  {pendingOrders.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="mt-8 space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Stat label="Occupancy" value={`${stats.occupancy_rate}%`} sub="This month" />
              <Stat label="Revenue" value={`$${stats.revenue_this_month.toLocaleString()}`} sub="This month · sample" />
              <Stat label="Nightly Rate" value={`$${stats.avg_nightly_rate}`} sub="Average" />
              <Stat label="Guest Rating" value={stats.guest_rating} sub="All platforms" />
              <div data-testid="owner-dashboard-payout-card"><Stat label="Next Payout" value={`$${stats.next_payout.toLocaleString()}`} sub="Estimated" /></div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Panel title="Upcoming Reservations" badge={DEMO_NOTE} testid="owner-reservations-panel">
                <ul className="divide-y divide-navy/8">
                  {upcoming_reservations.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-4 py-3.5">
                      <div>
                        <p className="text-sm font-medium text-navy">{r.guest}</p>
                        <p className="text-xs text-navy/50">{r.check_in} → {r.check_out} · {r.guests} guests · {r.platform}</p>
                      </div>
                      <span className={`px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${r.status === "confirmed" ? "bg-ocean-light text-ocean" : "bg-sand text-navy/60"}`}>{r.status}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Cleaning Schedule" badge={DEMO_NOTE} testid="owner-cleaning-panel">
                <ul className="divide-y divide-navy/8">
                  {cleaning_schedule.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-4 py-3.5">
                      <div>
                        <p className="text-sm font-medium text-navy">{c.type}</p>
                        <p className="text-xs text-navy/50">{c.date} · {c.team}</p>
                      </div>
                      <span className="bg-ocean-light px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-ocean">{c.status}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Inspection Reports" badge={DEMO_NOTE} testid="owner-inspections-panel">
                <ul className="divide-y divide-navy/8">
                  {inspection_reports.map((r) => (
                    <li key={r.id} className="py-3.5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-navy">{r.type}</p>
                        <p className="text-xs text-navy/45">{r.date}</p>
                      </div>
                      <p className="mt-1 text-xs text-navy/55">{r.result} · {r.photos} photos</p>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Owner Statements" badge={DEMO_NOTE} testid="owner-statements-panel">
                <ul className="divide-y divide-navy/8">
                  {statements.map((s) => (
                    <li key={s.id} className="py-3.5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-navy">{s.month}</p>
                        <p className="font-mono text-sm text-navy">Net ${s.net.toLocaleString()}</p>
                      </div>
                      <p className="mt-1 text-xs text-navy/50">Gross ${s.gross.toLocaleString()} · Mgmt ${s.management.toLocaleString()} · Cleaning ${s.cleaning.toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Inventory Alerts" badge={DEMO_NOTE} testid="owner-inventory-panel">
                <ul className="divide-y divide-navy/8">
                  {inventory_alerts.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-4 py-3.5">
                      <div>
                        <p className="text-sm font-medium text-navy">{a.item}</p>
                        <p className="text-xs text-navy/50">{a.note}</p>
                      </div>
                      <span className="bg-pink-soft px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-bermuda-dark">{a.level}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <div className="grid gap-6">
                <Panel title="Property Photographs" testid="owner-photos-panel"><ComingSoon icon={Camera} label="Photo library" /></Panel>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              <Panel title="Expenses" testid="owner-expenses-panel"><ComingSoon icon={Wallet} label="Expense tracking" /></Panel>
              <Panel title="Invoices & Documents" testid="owner-documents-panel"><ComingSoon icon={FolderOpen} label="Document vault" /></Panel>
              <Panel title="Property Notes" testid="owner-notes-panel"><ComingSoon icon={FileText} label="Shared property notes" /></Panel>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Panel title="Maintenance Requests" testid="owner-maintenance-list">
              {data.maintenance_requests.length === 0 ? (
                <p className="py-6 text-center text-sm text-navy/45">No maintenance requests yet.</p>
              ) : (
                <ul className="divide-y divide-navy/8">
                  {data.maintenance_requests.map((r) => (
                    <li key={r.id} className="py-3.5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-navy">{r.category} — {r.property_name}</p>
                        <span className="bg-sand px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-navy/60">{r.status}</span>
                      </div>
                      <p className="mt-1 text-xs text-navy/50">{r.urgency} · {r.created_at.slice(0, 10)}</p>
                      <p className="mt-1 text-xs text-navy/60">{r.description}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Panel title="Cleaning Requests" testid="owner-cleaning-list">
              {data.cleaning_requests.length === 0 ? (
                <p className="py-6 text-center text-sm text-navy/45">No cleaning requests yet.</p>
              ) : (
                <ul className="divide-y divide-navy/8">
                  {data.cleaning_requests.map((r) => (
                    <li key={r.id} className="py-3.5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-navy">{r.service_type} — {r.property_name}</p>
                        <span className="bg-sand px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-navy/60">{r.status}</span>
                      </div>
                      <p className="mt-1 text-xs text-navy/50">{r.preferred_date}{r.notes ? ` · ${r.notes}` : ""}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}

        {tab === "new-maintenance" && (
          <div className="mt-8">
            <Panel title="Report a Property Issue" testid="owner-new-maintenance-panel">
              <MaintenanceForm onDone={() => { load(); setTab("requests"); }} />
            </Panel>
          </div>
        )}

        {tab === "new-cleaning" && (
          <div className="mt-8">
            <Panel title="Request a Cleaning Service" testid="owner-new-cleaning-panel">
              <CleaningForm onDone={() => { load(); setTab("requests"); }} />
            </Panel>
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-8">
            <Panel title="Client Reviews" testid="owner-reviews-panel">
              {reviews.length === 0 ? (
                <p className="py-6 text-center text-sm text-navy/45">No reviews yet. Share thebermudacohost.com/reviews with happy clients.</p>
              ) : (
                <ul className="divide-y divide-navy/10">
                  {reviews.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-start justify-between gap-4 py-4" data-testid={`owner-review-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <Stars value={r.rating} size={13} />
                          <span className="text-sm font-medium text-navy">{r.name}</span>
                          <span className="bg-sand px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-navy/55">{r.service}</span>
                          <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${r.approved ? "bg-ocean-light text-ocean" : "bg-pink-soft text-bermuda-dark"}`}>
                            {r.approved ? "Live" : "Pending"}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-navy/70">"{r.text}"</p>
                        {!r.display_permission && <p className="mt-1 text-xs text-bermuda-dark">No display permission — cannot be published</p>}
                      </div>
                      {!r.approved && r.display_permission && (
                        <button
                          onClick={async () => { try { await api.post(`/owner/reviews/${r.id}/approve`); load(); toast.success("Review is live."); } catch (e) { toast.error(formatApiError(e)); } }}
                          data-testid={`approve-review-${r.id}`}
                          className="shrink-0 bg-navy px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark"
                        >
                          Approve
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Invite Past Clients" testid="owner-invites-panel">
              <p className="text-sm leading-relaxed text-navy/55">
                Send a personal review invitation by email. Past contacts from your website enquiries and ebook orders appear below —
                one tap sends a branded note linking to your review page. Each address can only be invited once.
              </p>
              <form
                onSubmit={(e) => { e.preventDefault(); sendInvite(manualInvite.email, manualInvite.name); }}
                className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
                data-testid="manual-invite-form"
              >
                <Input data-testid="manual-invite-name" value={manualInvite.name} onChange={(e) => setManualInvite({ ...manualInvite, name: e.target.value })} placeholder="Name (optional)" />
                <Input data-testid="manual-invite-email" required type="email" value={manualInvite.email} onChange={(e) => setManualInvite({ ...manualInvite, email: e.target.value })} placeholder="client@email.com" />
                <button type="submit" disabled={inviting[manualInvite.email]} data-testid="manual-invite-submit" className="bg-navy px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-60">
                  {inviting[manualInvite.email] ? "Sending…" : "Send Invite"}
                </button>
              </form>
              {contacts.length > 0 && (
                <ul className="mt-5 divide-y divide-navy/10" data-testid="invite-contacts-list">
                  {contacts.map((c) => (
                    <li key={c.email} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`invite-contact-${c.email}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-navy">{c.name}</p>
                        <p className="truncate text-xs text-navy/50">{c.email} · <span className="capitalize">{c.source.replace(/_/g, " ")}</span></p>
                      </div>
                      {c.invited ? (
                        <span className="bg-ocean-light px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-ocean" data-testid={`invited-badge-${c.email}`}>Invited</span>
                      ) : (
                        <button
                          onClick={() => sendInvite(c.email, c.name)}
                          disabled={inviting[c.email]}
                          data-testid={`invite-send-${c.email}`}
                          className="shrink-0 border border-navy/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory disabled:opacity-60"
                        >
                          {inviting[c.email] ? "Sending…" : "Send Invite"}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-8">
            <Panel title="Website Enquiries" testid="owner-leads-panel">
              <p className="mb-4 text-[11px] text-navy/45">
                Ebook orders live in the{" "}
                <button onClick={() => setTab("orders")} data-testid="leads-to-orders-link" className="font-medium text-bermuda-dark underline underline-offset-2">Orders tab</button>.
              </p>
              <div className="mb-4 flex flex-wrap gap-1.5" data-testid="leads-status-filters">
                {[["all", "All"], ["new", "New"], ["in_progress", "In Progress"], ["won", "Won"], ["closed", "Closed"]].map(([key, label]) => {
                  const count = key === "all" ? serviceLeads.length : serviceLeads.filter((l) => (l.status || "new") === key).length;
                  return (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      data-testid={`leads-filter-${key}`}
                      className={`border px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${statusFilter === key ? "border-navy bg-navy text-ivory" : "border-navy/15 text-navy/50 hover:border-navy/40 hover:text-navy"}`}
                    >
                      {label} · {count}
                    </button>
                  );
                })}
              </div>
              {filteredServiceLeads.length === 0 ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <Inbox size={22} strokeWidth={1.2} className="text-navy/30" />
                  <p className="mt-3 text-sm text-navy/50">{serviceLeads.length === 0 ? "No enquiries yet." : "No enquiries with this status."}</p>
                </div>
              ) : (
                <ul className="divide-y divide-navy/8">
                  {filteredServiceLeads.map((l) => <LeadRow key={l.id} l={l} ctx={rowCtx} />)}
                </ul>
              )}
            </Panel>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8">
            <div className="flex flex-wrap gap-3" data-testid="orders-stats">
              <span className="border border-bermuda-dark/30 bg-bermuda/15 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-bermuda-dark" data-testid="orders-pending-count">Awaiting Payment · {pendingOrders.length}</span>
              <span className="border border-sage/40 bg-sage/15 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sage" data-testid="orders-paid-count">Paid · {paidOrders.length}</span>
              <span className="border border-navy/15 bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-navy/60" data-testid="orders-revenue">Collected · ${ordersRevenue}</span>
              <button
                onClick={sendDigest}
                disabled={digestSending}
                data-testid="send-digest-btn"
                className="border border-navy/25 bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-ivory disabled:opacity-60"
              >
                {digestSending ? "Sending…" : "Email Me the Digest Now"}
              </button>
            </div>
            <div className="mt-5">
              <Panel title="Ebook Orders" testid="owner-orders-panel">
                {orderLeads.length === 0 ? (
                  <div className="flex flex-col items-center py-8 text-center">
                    <Wallet size={22} strokeWidth={1.2} className="text-navy/30" />
                    <p className="mt-3 text-sm text-navy/50">No orders yet.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-navy/8">
                    {orderLeads.map((l) => <LeadRow key={l.id} l={l} ctx={rowCtx} />)}
                  </ul>
                )}
              </Panel>
            </div>
          </div>
        )}

        {tab === "recs" && (
          <div className="mt-8">
            <Panel title="Recommendation Database" badge={`${recsList.length} places`} testid="owner-recs-panel">
              <p className="text-sm leading-relaxed text-navy/55">
                These places power the My Bermuda Stay planner. Add, edit or hide any recommendation — the planner updates instantly.
              </p>
              {!recForm ? (
                <button onClick={() => setRecForm({ ...EMPTY_REC })} data-testid="rec-add-btn" className="mt-4 bg-navy px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
                  Add Recommendation
                </button>
              ) : (
                <form onSubmit={saveRec} className="mt-5 grid gap-4 border border-navy/10 bg-sand-light/50 p-5" data-testid="rec-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name" required><Input data-testid="rec-form-name" required value={recForm.name} onChange={(e) => setRecForm({ ...recForm, name: e.target.value })} /></Field>
                    <Field label="Category"><Select data-testid="rec-form-category" value={recForm.category} onChange={(e) => setRecForm({ ...recForm, category: e.target.value })}>{REC_CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}</Select></Field>
                    <Field label="Parish"><Select data-testid="rec-form-parish" value={recForm.parish} onChange={(e) => setRecForm({ ...recForm, parish: e.target.value })}>{REC_PARISHES.map((p) => <option key={p} value={p}>{p}</option>)}</Select></Field>
                    <Field label="Price Level"><Select data-testid="rec-form-price" value={recForm.price_level} onChange={(e) => setRecForm({ ...recForm, price_level: e.target.value })}>{[[1, "$"], [2, "$$"], [3, "$$$"], [4, "$$$$ — Luxury"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
                    <Field label="Website"><Input data-testid="rec-form-website" value={recForm.website || ""} onChange={(e) => setRecForm({ ...recForm, website: e.target.value })} placeholder="https://…" /></Field>
                    <Field label="Booking URL"><Input data-testid="rec-form-booking" value={recForm.booking_url || ""} onChange={(e) => setRecForm({ ...recForm, booking_url: e.target.value })} placeholder="Optional" /></Field>
                    <Field label="Duration (hours)"><Input data-testid="rec-form-duration" type="number" step="0.5" min="0.5" value={recForm.duration_hours} onChange={(e) => setRecForm({ ...recForm, duration_hours: e.target.value })} /></Field>
                    <Field label="Latitude (map pin)"><Input data-testid="rec-form-lat" type="number" step="0.00001" value={recForm.lat ?? ""} onChange={(e) => setRecForm({ ...recForm, lat: e.target.value })} placeholder="e.g. 32.25219" /></Field>
                    <Field label="Longitude (map pin)"><Input data-testid="rec-form-lng" type="number" step="0.00001" value={recForm.lng ?? ""} onChange={(e) => setRecForm({ ...recForm, lng: e.target.value })} placeholder="e.g. -64.82168" /></Field>
                  </div>
                  <Field label="Description"><Textarea data-testid="rec-form-description" rows={2} value={recForm.description} onChange={(e) => setRecForm({ ...recForm, description: e.target.value })} /></Field>
                  {[["audiences", "Audiences", REC_AUDIENCES], ["ages", "Suitable Ages", REC_AGES], ["interests", "Interests", INTEREST_OPTIONS.map(([v]) => v)]].map(([key, label, opts]) => (
                    <div key={key}>
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/55">{label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {opts.map((o) => {
                          const arr = recForm[key] || [];
                          const on = arr.includes(o);
                          return (
                            <button key={o} type="button" data-testid={`rec-form-${key}-${o}`} onClick={() => setRecForm({ ...recForm, [key]: on ? arr.filter((x) => x !== o) : [...arr, o] })} className={`border px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${on ? "border-navy bg-navy text-ivory" : "border-navy/15 text-navy/50 hover:border-navy/40"}`}>
                              {o.replace(/_/g, " ")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-5">
                    {[["rainy_day", "Rainy day"], ["beach_day", "Beach day"], ["active", "Live on site"]].map(([k, label]) => (
                      <label key={k} className="flex cursor-pointer items-center gap-2 text-xs text-navy/70">
                        <input type="checkbox" data-testid={`rec-form-${k}`} checked={!!recForm[k]} onChange={(e) => setRecForm({ ...recForm, [k]: e.target.checked })} className="h-4 w-4 accent-bermuda-dark" />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <SubmitButton loading={recSaving} testid="rec-form-submit">{recForm.id ? "Save Changes" : "Add Recommendation"}</SubmitButton>
                    <button type="button" onClick={() => setRecForm(null)} data-testid="rec-form-cancel" className="px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/50 hover:text-navy">Cancel</button>
                  </div>
                </form>
              )}
              <ul className="mt-5 divide-y divide-navy/8" data-testid="recs-list">
                {recsList.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`rec-row-${r.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-navy">{r.name}{!r.active && <span className="text-bermuda-dark"> (hidden)</span>}</p>
                      <p className="truncate text-xs capitalize text-navy/50">{r.category} · {r.parish} · {"$".repeat(r.price_level)} · ~{r.duration_hours}h · {(r.audiences || []).join(", ")}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button onClick={() => setRecForm({ ...EMPTY_REC, ...r })} data-testid={`rec-edit-${r.id}`} className="border border-navy/25 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-ivory">Edit</button>
                      <button onClick={() => toggleRecActive(r)} data-testid={`rec-toggle-${r.id}`} className="border border-navy/25 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-ivory">{r.active ? "Hide" : "Show"}</button>
                      <button onClick={() => deleteRec(r.id)} data-testid={`rec-delete-${r.id}`} className="border border-bermuda-dark/40 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-bermuda-dark transition-colors hover:bg-bermuda-dark hover:text-white">Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        )}

        {tab === "analytics" && (
          <div className="mt-8 space-y-6" data-testid="owner-analytics">
            <div className="grid gap-4 sm:grid-cols-2">
              <Stat label="Saved Trips" value={tripStats ? tripStats.total_trips : "—"} sub="Plans created in My Bermuda Stay" />
              <Stat
                label="Average Stay Length"
                value={tripStats?.avg_stay_days ? `${tripStats.avg_stay_days} days` : "—"}
                sub={tripStats ? `From ${tripStats.trips_with_dates} trips with travel dates` : ""}
              />
            </div>
            <Panel title="Most-Saved Places" badge="Partnership signals" testid="owner-top-places">
              <p className="text-sm leading-relaxed text-navy/55">
                The places guests add to their trips most often — strong candidates for preferred-partner arrangements.
              </p>
              {!tripStats || tripStats.top_places.length === 0 ? (
                <p className="mt-4 border border-dashed border-navy/20 bg-sand-light/60 p-6 text-center text-sm text-navy/55" data-testid="top-places-empty">
                  No saved trips yet — rankings will appear here as guests save places to their plans.
                </p>
              ) : (
                <>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[11px] text-navy/45">Tap Pitch to open a ready-to-send email, or copy the whole list.</p>
                    <button
                      onClick={async () => {
                        const lines = tripStats.top_places.map((p, i) => `${i + 1}. ${p.name} — ${p.count} ${p.count === 1 ? "save" : "saves"} (${[p.category, p.parish].filter(Boolean).join(" · ")})`);
                        const text = [
                          "Most-saved places — My Bermuda Stay",
                          `${tripStats.total_trips} saved trips · average stay ${tripStats.avg_stay_days ?? "—"} days (from ${tripStats.trips_with_dates} trips with dates)`,
                          "",
                          ...lines,
                        ].join("\n");
                        try { await navigator.clipboard.writeText(text); toast.success("List copied — paste it into any email or note."); } catch { toast.error("Copy failed — please select and copy manually."); }
                      }}
                      data-testid="copy-top-places-btn"
                      className="flex items-center gap-1.5 border border-navy/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy transition-colors hover:bg-navy hover:text-ivory"
                    >
                      <ClipboardCopy size={12} /> Copy List
                    </button>
                  </div>
                  <ul className="mt-4 space-y-2.5" data-testid="top-places-list">
                    {tripStats.top_places.map((p, i) => {
                      const max = tripStats.top_places[0].count || 1;
                      const subject = `Partnership idea — ${p.name} × The Bermuda Co-Host`;
                      const body = [
                        `Hello ${p.name} team,`,
                        "",
                        "I run The Bermuda Co-Host, a local Bermuda hospitality and guest-concierge service. Guests using our My Bermuda Stay trip planner have been saving your place to their island itineraries — and I would love to send more of them your way.",
                        "",
                        `From the planner so far: ${p.count} guest ${p.count === 1 ? "save" : "saves"}${tripStats.avg_stay_days ? `, with an average stay of ${tripStats.avg_stay_days} days on the island` : ""}.`,
                        "",
                        "Could we explore a preferred-partner arrangement — priority bookings for our guests, with a fair referral thank-you for introductions?",
                        "",
                        "Warm regards,",
                        "The Bermuda Co-Host",
                        "+1 (441) 519-1794 · thebermudacohost.com",
                      ].join("\n");
                      return (
                        <li key={p.id} data-testid={`top-place-${i}`}>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium text-navy">
                              <span className="mr-2 font-serif text-navy/40">{i + 1}.</span>
                              {p.name}
                            </p>
                            <div className="flex shrink-0 items-center gap-3">
                              <p className="text-[11px] capitalize text-navy/50">
                                {p.category}{p.parish ? ` · ${p.parish}` : ""} · <span className="font-semibold text-navy">{p.count} {p.count === 1 ? "save" : "saves"}</span>
                              </p>
                              <a
                                href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                                data-testid={`pitch-email-${i}`}
                                className="flex items-center gap-1.5 border border-bermuda-dark/40 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-bermuda-dark transition-colors hover:bg-bermuda-dark hover:text-white"
                              >
                                <Mail size={11} /> Pitch
                              </a>
                            </div>
                          </div>
                          <div className="mt-1 h-1.5 w-full bg-sand-light">
                            <div className="h-full bg-bermuda" style={{ width: `${Math.max((p.count / max) * 100, 4)}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </Panel>
          </div>
        )}
      </div>
    </main>
  );
}
